import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials as mockTestimonials } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t } = useLanguage();

  const testimonialsText = t('testimonials');
  const testimonials = Array.isArray(testimonialsText?.items) ? testimonialsText.items : mockTestimonials;
  const testimonialsLength = testimonials?.length || 1;

  const goToNext = () => {
    if (isAnimating || !testimonials?.length) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonialsLength);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToPrev = () => {
    if (isAnimating || !testimonials?.length) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonialsLength) % testimonialsLength);
    setTimeout(() => setIsAnimating(false), 500);
  };

  useEffect(() => {
    const interval = setInterval(goToNext, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="bg-[#0a0a0b] py-20">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-400/10 text-teal-400 text-sm font-medium px-4 py-1.5 rounded-full">
            {t('common.testimonials') || 'Testimonials'}
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mt-6">
            {testimonialsText?.title || 'What Our Users Say'}
          </h2>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {/* Main Testimonial Card */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <Quote className="absolute top-6 left-6 w-12 h-12 text-teal-400/20" />
            
            <div className={`transition-opacity duration-500 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
              <p className="text-xl md:text-2xl text-white leading-relaxed mb-8 relative z-10">
                "{testimonials[currentIndex]?.quote}"
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-black font-bold">
                  {testimonials[currentIndex]?.author?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-white font-medium">{testimonials[currentIndex]?.author}</p>
                  {testimonials[currentIndex]?.company && (
                    <p className="text-gray-400 text-sm">{testimonials[currentIndex].company}</p>
                  )}
                  {testimonials[currentIndex]?.role && (
                    <p className="text-gray-400 text-sm">{testimonials[currentIndex].role}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={goToPrev}
              className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <span className="text-gray-500 text-sm">
              {currentIndex + 1} / {testimonialsLength}
            </span>
            
            <button
              onClick={goToNext}
              className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <button className="bg-teal-400 hover:bg-teal-500 text-black px-8 py-3 rounded-lg font-medium transition-colors">
            {t('common.getStartedFree') || 'Get Started for Free'}
          </button>
          <button className="border border-gray-700 text-white hover:border-teal-400 px-8 py-3 rounded-lg font-medium transition-colors">
            {t('common.scheduleDemo') || 'Schedule a Demo'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
